<template>
  <div>
    <div class="col-12 p0 py2 my1 break-word">
      <h2 class="m0 caps">
        Planning
      </h2>
      <span class="h3 m0">Sélectionnez le jour de votre séance</span>
    </div>

    <div v-if="sessionBannerMessage" class="flex h6 center p2 red">
      {{ sessionBannerMessage }}
    </div>
    <Calendar
      v-if="renderComponent"
      ref="calendar"
      :attributes="calendarAttributes"
      expanded
      :disabled-dates="disabledDatesList"
      :max-date="maxDate"
      :min-date="minDate"
      @update:pages="onPagesUpdate"
      @dayclick="onDayClick"
    />

    <div class="col-12 py3 flex justify-end items-center">
      <Button v-if="!$store.getters.isRaquettes" class="col-4 sm-col-6" :action="handleBack" text="Precedent" color="grey-bg" />
      <Button id="button-confirm-day" class="col-4 sm-col-6 ml2" :disabled="!selectedDate" :action="confirmDay" text="Suivant" />
    </div>
  </div>
</template>

<script>
import { getMonth, getYear, getDate, format, isAfter, isBefore, isSameDay, startOfMonth, endOfMonth, addMonths, parse } from 'date-fns';
import { Calendar } from 'v-calendar';
import 'v-calendar/style.css';

import Button from '@/components/elements/Button';
import { getSessions, getAvailableDates } from '@/utils/requests';
import { changeToPage } from '@/utils/logics';
import handleSubscription from '@/utils/amplify';
import { frenchTime } from '@/utils/utils';

export default {
  components: { Button, Calendar },
  data () {
    return {
      sessions: this.$store.state.sessions,
      datesGroupedByMonthAndYear: {},
      monthSelected: null,
      renderComponent: true,
      selectedDate: null,
      is_first_request: true,
      sessionBannerMessage: '',
      /** Dedupe v-calendar v3 `update:pages` (watchEffect can emit often for the same month). */
      lastHandledPageKey: null,
      fetchingPageKey: null
    };
  },
  computed: {
    availableDates () {
      return Object.keys(this.$store.state.availableDates).filter(key => this.$store.state.availableDates[key].length);
    },
    isAmplifyWorkspace () {
      return this.isInternalSession || ['postgres'].includes(this.siteInfo.horanetSource);
    },
    /** v-calendar v3 expects an array of Date/range objects, not { days: [] }. */
    disabledDatesList () {
      if (!this.monthSelected) {
        return [];
      }
      const { month, year } = this.monthSelected;

      let sessionDays = { days: [] };

      if (this.availableDates && this.isAmplifyWorkspace) {
        sessionDays.days = this.availableDates.map(date => parseInt(format(parse(date, 'yyyy-MM-dd', new Date()), 'dd')));
      }

      if (!this.isAmplifyWorkspace) {
        sessionDays = this.datesGroupedByMonthAndYear[`${year}-${month}`] || { days: [] };
      }

      const date = new Date(year, month - 1);
      const firstDayOfMonth = getDate(startOfMonth(date));
      const lastDayOfMonth = getDate(endOfMonth(date));
      const list = [];
      for (let dayNum = firstDayOfMonth; dayNum <= lastDayOfMonth; dayNum += 1) {
        if (!sessionDays.days.includes(dayNum)) {
          list.push(new Date(year, month - 1, dayNum));
        }
      }
      return list;
    },
    calendarAttributes () {
      if (!this.selectedDate) {
        return [];
      }
      return [{
        key: 'selected',
        highlight: true,
        dates: this.selectedDate
      }];
    },
    siteInfo () {
      return this.$store.state.siteInfo;
    },
    isImpersonateMode () {
      return this.$store.getters.isImpersonateMode;
    },
    isInternalSession () {
      return this.siteInfo && this.siteInfo.is_internal_session;
    },
    minDate () {
      return format(new Date(), 'yyyy-MM-dd');
    },
    maxDate () {
      const period = this.$store.state.cartItem.participants[0].tariff && this.$store.state.cartItem.participants[0].tariff.period ? this.$store.state.cartItem.participants[0].tariff.period : null;

      let date = addMonths(new Date(), this.siteInfo.tda_settings.calendarMonths);

      if (period) {
        date = new Date(period.validityEndDate);
      }

      return format(date, 'yyyy-MM-dd');
    }
  },
  async created () {
    const { cartItem } = this.$store.state;
    cartItem.selectedDate = null;
    this.$store.commit('setCartItem', { ...cartItem });
    this.$store.commit('setCurrentParticipant', null);
  },
  methods: {
    changeToPage,
    frenchTime,
    showNoSessionMessage (hasSessions, message) {
      this.sessionBannerMessage = hasSessions ? '' : (message || '');
    },
    async getAmplifyAvailableDates (year, month) {
      let date = {
        month: month,
        year: year
      };

      const { cartItem } = this.$store.state;

      if (this.$store.state.amplifySubscription && !Object.keys(this.$store.state.amplifySubscription).length && cartItem.selectedMonth === `${year}-${month}`) {
        return date;
      }

      const variables = {
        workspace: this.siteInfo.workspace,
        date: `${year}-${String(month).padStart(2, '0')}`,
        isFirstRequest: this.is_first_request,
        participantsAmount: cartItem.participants.length
      };
      if (!this.isInternalSession && ['postgres'].includes(this.siteInfo.horanetSource)) {
        variables.codeSiteComptage = this.siteInfo.code_site_comptage;
        variables.productReference = this.$store.state.offerInfo.horanet_product.horanet_product_reference;
      }
      if (this.isInternalSession) {
        variables.productUuid = this.$store.state.product.uuid;
      }
      const response = await getAvailableDates(variables);
      const payload = response?.data;

      if (!payload) {
        this.$store.commit('setLoading', false);
        this.showNoSessionMessage(false, response?.message || 'Pas de créneau disponible');
        return date;
      }
      this.is_first_request = false;

      const { availableDates, message, date: serverDate, productServices } = payload;
      this.$store.commit('setAvailableDates', availableDates);
      let errorMessage = null;
      if (!this.availableDates || (this.availableDates && !this.availableDates.length)) {
        errorMessage = message;
      }
      date = serverDate;
      this.$store.commit('setProductServices', productServices);
      this.$store.commit('setLoading', false);
      if (errorMessage) {
        this.showNoSessionMessage(false, errorMessage);
      } else {
        this.sessionBannerMessage = '';
      }
      cartItem.selectedMonth = `${date.year}-${date.month}`;
      this.$store.commit('setCartItem', { ...cartItem });
      return date;
    },
    async formatSessions (m, y) {
      this.$store.commit('setLoading', true);
      const date = {
        month: m,
        year: y
      };

      if (this.isAmplifyWorkspace) {
        return this.getAmplifyAvailableDates(y, m);
      }

      const json = await getSessions({ month: m, year: y, is_first_request: this.is_first_request });

      if (json.getReservations) {
        const { sessions, month, year, hasSessions, message } = json.getReservations;
        if (month) {
          date.month = month;
          date.year = year;
        }
        this.is_first_request = false;
        this.$store.commit('setSessions', hasSessions ? sessions : null);
        this.showNoSessionMessage(hasSessions, message);
      }
      // END OF LOADING
      this.$store.commit('setLoading', false);

      this.datesGroupedByMonthAndYear = this.groupSessions();

      return date;
    },
    /** v-calendar v3 emits `update:pages` with Page[] instead of `update:from-page`. */
    async onPagesUpdate (pages) {
      const page = pages && pages[0];
      if (!page) {
        return;
      }
      const pageKey = `${page.year}-${page.month}`;
      if (this.lastHandledPageKey === pageKey || this.fetchingPageKey === pageKey) {
        return;
      }
      this.fetchingPageKey = pageKey;
      const { cartItem } = this.$store.state;
      cartItem.selectedMonth = `${page.year}-${page.month}`;
      this.$store.commit('setCartItem', { ...cartItem });
      try {
        const date = await this.formatSessions(page.month, page.year);
        this.lastHandledPageKey = pageKey;
        const newDate = new Date(date.year, date.month - 1);
        this.monthSelected = { year: date.year, month: date.month };
        if (!isAfter(new Date(this.minDate), new Date(this.maxDate))) {
          this.$refs.calendar?.move?.(newDate);
        }
      } catch (err) {
        console.warn('Calendar: failed to load sessions for page', pageKey, err);
        this.lastHandledPageKey = pageKey;
        this.monthSelected = { year: page.year, month: page.month };
        this.$store.commit('setLoading', false);
      } finally {
        this.fetchingPageKey = null;
        if (this.isAmplifyWorkspace) {
          await handleSubscription();
        }
      }
    },
    onDayClick (day) {
      if (day.isDisabled) {
        return;
      }
      this.selectedDate = day.noonDate;
    },
    handleBack () {
      this.changeToPage('Participants');
    },
    confirmDay () {
      const { cartItem } = this.$store.state;
      cartItem.selectedDate = this.selectedDate;
      this.$store.commit('setCartItem', { ...cartItem });
      this.changeToPage('Session');
    },
    groupSessions () {
      const datesGroupedByMonthAndYear = {};
      const { items } = this.$store.state.sessions || {};

      if (items && items.length) {
        items.map(session => {
          const date = new Date(session.start_date);
          const now = new Date();

          if (isSameDay(now, date) && isBefore(date, now) && !this.isImpersonateMode && this.isInternalSession) {
            return session;
          }

          const month = getMonth(date) + 1;
          const year = getYear(date);
          const day = getDate(date);

          const objectKey = `${year}-${month}`;
          if (!datesGroupedByMonthAndYear.hasOwnProperty(objectKey)) {
            datesGroupedByMonthAndYear[objectKey] = { days: [] };
          }

          if (!datesGroupedByMonthAndYear[objectKey].days.includes(day)) {
            datesGroupedByMonthAndYear[objectKey].days.push(day);
          }

          return session;
        });
      }

      return datesGroupedByMonthAndYear;
    }
  }
};
</script>
